/* @ngInject */
function NgsTicketsManagerService(
  $log,
  NgsTicketsApiService,
  TicketsStoreService,
  errorParser,
  LoginData,
  userService,
  LOCAL_TICKET_STATUS,
  GravitySettings
) {
  const LOG_PREFIX = '[7S.NgsTickets]';

  /**
   * @typedef  {object} TicketMetadata
   * @property {string} cpvUuid
   * @property {string} deliveryPlatform
   * @property {string} paymentMethod
   * @property {string} product
   * @property {string} requestUuid
   * @property {string} uuid
   * @property {object[]} sources
   *
   * @param {TicketMetadata} ticketMeta
   *
   * @returns {TicketMetadata}
   */
  const getNgsTicketMetadata = (ticketMeta) => {
    const ticketMetadata = {
      ...ticketMeta
    };
    const ticketMetaSources = ticketMeta.sources;

    const betshopSource = ticketMetaSources.find(({ type }) => type === 'betshop');
    const betshopGroup = LoginData.betshopGroup;
    betshopSource.name = LoginData.betshop.name;
    betshopSource.displayId = LoginData.betshop.displayId;
    betshopSource.city = LoginData.betshop.city;
    betshopSource.groupName = betshopGroup?.name;
    betshopSource.groupId = betshopGroup?.id;

    const operatorSource = ticketMetaSources.find(({ type }) => type === 'operator');
    const operator = userService.getData().user;
    operatorSource.firstName = operator.firstName;
    operatorSource.lastName = operator.lastName;
    operatorSource.username = operator.username;

    const deviceSource = ticketMetaSources.find(({ type }) => type === 'device');
    deviceSource.name = LoginData.device.name;

    const cashRegisterSource = {
      type: 'cashRegister',
      uuid: LoginData.device.cashRegisterUuid
    };
    ticketMetadata.sources = [
      ...ticketMetadata.sources,
      cashRegisterSource
    ];

    ticketMetadata.origins = {
      deviceUuid: LoginData.device.uuid
    };

    return ticketMetadata;
  };

  /**
   * @param {TicketMetadata} ticketMeta
   *
   * @returns {{ tpToken: string, sources: object[] }}
   */
  const getNgsTicketClientVal = (ticketMeta) => {
    const ngsTicketData = GravitySettings.getByKey('module.NgsTickets');
    const clientValData = {
      tpToken: ngsTicketData.thirdPartyToken
    };
    const ticketMetadata = getNgsTicketMetadata(ticketMeta);
    const ticketMetadataSources = ['betshop', 'operator'];

    clientValData.sources = ticketMetadata.sources.filter(({ type }) => ticketMetadataSources.includes(type));

    return clientValData;
  };

  /**
   * @typedef  {object} NgsTicketMetadata
   * @property {string} cpvUuid
   * @property {string} deliveryPlatform
   * @property {string} product
   * @property {string} requestUuid
   * @property {string} uuid
   * @property {{type: "player", uuid: string, email: string}[]} sources
   *
   * @param {TicketMetadata} ticketMeta
   *
   * @returns {TicketMetadata}
   * @returns {NgsTicketMetadata}
   */
  const getNgsTicketPlayerMetadata = (ticketMeta) => {
    const ngsTicketData = GravitySettings.getByKey('module.NgsTickets');
    const ticketMetadata = {
      ...ticketMeta
    };
    const playerSource = {
      type: 'player',
      uuid: ngsTicketData.player.uuid,
      email: ngsTicketData.player.email
    };

    delete ticketMetadata.paymentMethod;
    ticketMetadata.sources = [
      playerSource
    ];

    ticketMetadata.origins = {
      deviceUuid: LoginData.device.uuid
    };

    return ticketMetadata;
  };

  /**
   * @typedef  {object} NgsTicketCancelPayload
   * @property {{id: string}} ticket
   * @property {string} ticketPin
   * @property {object} [loyaltyCard]
   * @property {TicketMetadata} metadata
   * @property {string} thirdPartyToken
   * @property {string} clientVal
   *
   * @param {SevenTicket} ticket
   * @param {TicketMetadata} ticketMeta
   *
   * @returns {NgsTicketCancelPayload}
   */
  const getNgsTicketCancelPayload = (ticket, ticketMeta) => {
    const payload = {
      ticket: {
        id: ticket.getDisplayId()
      },
      ticketPin: ticket.localPin,
      loyaltyCard: ticket.loyaltyDetails || null,
      metadata: getNgsTicketMetadata(ticketMeta)
    };

    return payload;
  };

  /**
   * @param {SevenTicket} ticket
   * @param {TicketMetadata} ticketMeta
   *
   * @typedef  {object} NgsTicketPayoutPayload
   * @property {{id: string}} ticket
   * @property {string} ticketPin
   * @property {object} [loyaltyCard]
   * @property {TicketMetadata} metadata
   * @property {number} payoutAmount
   * @property {string} thirdPartyToken
   * @property {string} clientVal
   * @property {object} tax
   *
   * @returns {NgsTicketPayoutPayload}
   */
  const getNgsTicketPayoutPayload = (ticket, ticketMeta) => {
    const ngsTicketData = GravitySettings.getByKey('module.NgsTickets');
    const payload = {
      ticket: {
        id: ticket.getDisplayId()
      },
      ticketPin: ticket.localPin,
      loyaltyCard: ticket.loyaltyDetails || null,
      metadata: getNgsTicketPlayerMetadata(ticketMeta),
      payoutAmount: ticket.getPossiblePayoutAmount(),
      thirdPartyToken: ngsTicketData.thirdPartyToken,
      clientVal: JSON.stringify(getNgsTicketClientVal(ticketMeta))
    };

    if (ticket.taxData) {
      payload.tax = ticket.taxData;
    }

    return payload;
  };

  /**
   * @param {Error} error
   * @param {SevenTicket} savedTicket
   * @param {boolean} ticketCheckerActive
   * @param {string} errorCode
   *
   * @returns {object}
   */
  const handleNgsTicketResponseError = (error, savedTicket, { ticketCheckerActive, errorCode }) => {
    const { upstream_code: upstreamCode, upstream_message: upstreamMessage } = errorParser.parseUpstream(error);
    const errorData = {
      error: true,
      type: 'warning',
      upstream_message: upstreamMessage,
      upstream_code: upstreamCode,
      localTicket: savedTicket,
      serverResponse: error.data,
      code: errorCode
    };
    const isTicketStatusPending = savedTicket.localStatus === LOCAL_TICKET_STATUS.PENDING;

    if (ticketCheckerActive && error?.status === -1) {
      savedTicket.startTicketChecker();
    }

    if ((error?.status <= 0 || error?.status >= 499) && !isTicketStatusPending) {
      errorData.error = false;

      return errorData;
    }

    if (isTicketStatusPending) {
      TicketsStoreService.handleFailedAction({
        response: error.data,
        request: savedTicket
      });
    }

    return errorData;
  };

  /**
   * @param {string} betshopId
   * @param {string} product
   *
   * @returns {boolean}
   */
  this.isNgsWebTicket = ({ betshopId, product }) => {
    const ngsProducts = [
      'LuckySix',
      'LuckyX',
      'GreyhoundRaces',
      'VirtualGreyhoundRaces',
      'HorseRaces',
      'VirtualHorseRaces',
      'VirtualDragRaces',
      'VirtualMotorcycleSpeedway',
      'SlotCarRaces',
      'NextSix',
      'Keno',
      'VirtualPenaltyShootout',
      'NVNVLightningLuckySix'
    ];

    return ngsProducts.includes(product) && !betshopId;
  };

  /**
   * @param {SevenTicket} ticket
   * @param {TicketMetadata} ticketMeta
   * @param {object} ticketRoutes
   * @param {string} reqUuid
   * @param {boolean} ticketCheckerActive
   *
   * @returns {Promise}
   */
  this.cancelTicket = ({ ticket, ticketMeta, ticketRoutes }, { reqUuid, ticketCheckerActive }) => (
    TicketsStoreService.saveSentAction(ticket, ticketRoutes).then((savedTicket) => {
      const productId = ticketMeta.cpvUuid;
      const payload = getNgsTicketCancelPayload(ticket, ticketMeta);

      $log.info(`${LOG_PREFIX} Sending ticket cancel request`, {
        payload,
        request_id: reqUuid,
        ticket_code: ticket.getDisplayId(),
        product_displayid: ticket.product,
        code: 'S_TICKET_CANCEL_NGS_REQUEST'
      });

      return NgsTicketsApiService.cancelTicket(productId, payload)
        .then((response) => {
          const shouldStartTicketChecker = savedTicket.localStatus === LOCAL_TICKET_STATUS.PENDING && ticketCheckerActive;

          $log.info(`${LOG_PREFIX} Ticket cancel response success`, {
            code: 'S_TICKET_CANCEL_NGS_SUCCESS',
            response: response.data,
            product_displayid: ticket.product,
            request_id: reqUuid,
            ticket_code: ticket.getDisplayId()
          });

          if (shouldStartTicketChecker) {
            savedTicket.startTicketChecker();
          }

          return response;
        })
        .catch((error) => {
          const { upstream_code: upstreamCode, upstream_message: upstreamMessage } = errorParser.parseUpstream(error);

          $log.error(`${LOG_PREFIX} Ticket cancel has failed`, {
            product_displayid: ticket.product,
            serverResponse: error.data,
            request_id: reqUuid,
            ticket_code: ticket.getDisplayId(),
            code: 'S_TICKET_CANCEL_NGS_ERROR',
            upstream_code: upstreamCode,
            upstream_message: upstreamMessage,
            duration: error.config.duration
          });

          return handleNgsTicketResponseError(error, savedTicket, { ticketCheckerActive, errorCode: 'S_TICKET_CANCEL_NGS_FAILED' });
        });
    })
  );

  /**
   * @param {SevenTicket} ticket
   * @param {TicketMetadata} ticketMeta
   * @param {object} ticketRoutes
   * @param {boolean} ticketCheckerActive
   *
   * @returns {Promise}
   */
  this.ticketPayout = ({ ticket, ticketMeta, ticketRoutes }, { ticketCheckerActive }) => (
    TicketsStoreService.saveSentAction(ticket, ticketRoutes).then((savedTicket) => {
      const productId = ticketMeta.cpvUuid;
      const requestUuid = ticketMeta.requestUuid;
      const payload = getNgsTicketPayoutPayload(ticket, ticketMeta);

      $log.info(`${LOG_PREFIX} Sending ticket payout request`, {
        payload,
        request_id: requestUuid,
        ticket_code: ticket.getDisplayId(),
        product_displayid: ticket.product,
        code: 'S_TICKET_PAYOUT_NGS_REQUEST'
      });

      return NgsTicketsApiService.payoutTicket(productId, payload)
        .then((response) => {
          const shouldStartTicketChecker = savedTicket.localStatus === LOCAL_TICKET_STATUS.PENDING && ticketCheckerActive;

          $log.info(`${LOG_PREFIX} Ticket payout response success`, {
            code: 'S_TICKET_PAYOUT_NGS_SUCCESS',
            response: response.data,
            product_displayid: ticket.product,
            request_id: requestUuid,
            ticket_code: ticket.getDisplayId()
          });

          if (shouldStartTicketChecker) {
            savedTicket.startTicketChecker();
          }

          return response;
        })
        .catch((error) => {
          const { upstream_code: upstreamCode, upstream_message: upstreamMessage } = errorParser.parseUpstream(error);

          $log.error(`${LOG_PREFIX} Ticket payout has failed`, {
            product_displayid: ticket.product,
            serverResponse: error.data,
            request_id: requestUuid,
            ticket_code: ticket.getDisplayId(),
            code: 'S_TICKET_PAYOUT_NGS_ERROR',
            upstream_code: upstreamCode,
            upstream_message: upstreamMessage,
            duration: error.config.duration
          });

          return handleNgsTicketResponseError(error, savedTicket, { ticketCheckerActive, errorCode: 'S_TICKET_PAYOUT_NGS_FAILED' });
        });
    })
  );
}

export default NgsTicketsManagerService;
